import classNames from 'classnames'
import React, {
  FunctionComponent,
  InputHTMLAttributes,
  useEffect,
  useState
} from 'react'

import { useFormContext } from '@f/elements/FormText/useFormContext'
import {
  InputValidation,
  isCrossFieldValidation,
  isFailedCrossFieldValidation,
  isFailedSingleFieldValidation
} from '@f/elements/FormText/ValidationUtils'

export type setFormElementsProps = (props: {
  name: string
  invalidRule: string
  value?: string
  custom?: string
}) => void

export interface FormTextProps extends InputHTMLAttributes<HTMLInputElement> {
  formElement: boolean
  inputField?: boolean
  name: string
  value?: string
  defaultValue?: string
  inputProps?: any
  validation?: InputValidation[]
  formElements?: any
  setFormElements?: setFormElementsProps
  isHorizontal?: boolean
  autoFocus?: boolean
  locationFinderPlaceholder?: string
  error?: string
}

const ENTER_KEY_VALUE = 'Enter'

const FormText: FunctionComponent<FormTextProps> = ({
  formElement,
  name,
  type = 'text',
  defaultValue,
  value,
  placeholder,
  className,
  onChange,
  onFocus,
  onBlur,
  inputProps,
  validation: listOfValidations = [],
  formElements,
  setFormElements,
  isHorizontal,
  autoFocus,
  locationFinderPlaceholder,
  error,
  disabled,
  children,
  inputField,
  ...props
}) => {
  const formContext = useFormContext()
  const [localValue, setLocalValue] = useState<string>(
    value || defaultValue || ''
  )
  const [displayError, setDisplayError] = useState<string | undefined>(error)
  const [canValidate, setCanValidate] = useState(false)
  const [focus, setFocus] = useState(false)

  useEffect(() => {
    const isValueExternallyControlled = typeof value === 'string'
    if (isValueExternallyControlled) {
      setLocalValue(value || '')

      validate(value)
    }
  }, [value])

  useEffect(() => {
    if (setFormElements) {
      setFormElements({
        name,
        value: localValue,
        invalidRule: ''
      })
    }
  }, [localValue])

  const overwriteErrorsOnPropChange = () => {
    setDisplayError(error)
  }

  useEffect(overwriteErrorsOnPropChange, [error])

  const validate = (value?: string) => {
    const valueToCheck = value || localValue
    const brokenRule = listOfValidations.find(validation =>
      isCrossFieldValidation(validation)
        ? isFailedCrossFieldValidation(
            valueToCheck,
            formContext.formElements,
            validation
          )
        : isFailedSingleFieldValidation(valueToCheck, validation)
    )
    const errorMessage = brokenRule?.message
    setDisplayError(errorMessage)

    if (setFormElements) {
      const formPayload = {
        name,
        invalidRule: brokenRule?.rule || '',
        value: brokenRule ? undefined : valueToCheck
      }
      setFormElements(formPayload)
    }
  }

  const onInputChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    if (!value) {
      setLocalValue(event.target.value)
    }

    validate(event.target.value)

    if (onChange) {
      onChange(event)
    }
  }

  useEffect(validate, [])

  function validateOnInputLeave(event: any) {
    setFocus(false)

    setCanValidate(true)
    validate(event.target.value)

    if (onBlur) {
      onBlur(event)
    }
  }

  const onInputBlur = (event: React.SyntheticEvent<HTMLInputElement>) => {
    validateOnInputLeave(event)
  }

  const onEnterPress = (event: React.KeyboardEvent) => {
    if (event.key === ENTER_KEY_VALUE) {
      validateOnInputLeave(event)
    }
  }

  const onInputFocus = (event: React.FocusEvent<HTMLInputElement>) => {
    setFocus(true)
    window._wt({
      category: 'input',
      action: 'focus',
      values: [{ type: 'name', value: name }]
    })

    if (onFocus) {
      onFocus(event)
    }
  }

  const shouldIndicateValidation =
    (formElements && formElements.status === 'validating') ||
    canValidate ||
    error

  const labelProps = props as any

  const textInput = (
    <>
      <label
        className={classNames(
          className,
          'f-d-bl',
          'f-pos-rel',
          'f-c-fo-pri',
          'f-p-h-020',
          'FormText-wrapper',
          {
            'FormText--disabled': disabled,
            'is-validating': shouldIndicateValidation,
            'f-c-bo-war': canValidate && displayError,
            'f-c-bo-suc400': canValidate && !displayError
          }
        )}
        {...labelProps}>
        <span
          className={classNames(
            '[ f-pos-ab f-c-fo-mut f-t-body ] FormTextLabel',
            {
              'has-value':
                localValue !== '' || focus || locationFinderPlaceholder
            }
          )}>
          {placeholder}
        </span>
        <input
          name={name}
          type={type}
          className='[ f-p-a-0 ] FormText'
          placeholder={locationFinderPlaceholder}
          value={localValue}
          onChange={onInputChange}
          onFocus={onInputFocus}
          onBlur={onInputBlur}
          onKeyPress={onEnterPress}
          autoFocus={autoFocus}
          disabled={disabled}
          {...inputProps}
        />
        {children}
      </label>
      {shouldIndicateValidation && displayError && (
        <span className='[ f-d-bl f-m-b-020 f-c-fo-war ] FormError-wrapper'>
          {displayError}
        </span>
      )}
    </>
  )

  if (isHorizontal) {
    return <div className='[ f-pos-rel ] full-width:s'>{textInput}</div>
  }

  return textInput
}

export default FormText
